"use client";

import { Button, NumberInput, Popover, Stack, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  extractPdfPage,
  savePdfDownload,
  singlePageFilename,
  type PreviewPdfDocument,
} from "./actions/pdfPageDownload";

export default function PdfPageDownload({
  pdf, filename, active,
}: {
  pdf: PreviewPdfDocument | null;
  filename: string;
  active: boolean;
}) {
  const [opened, setOpened] = useState(false);
  const [pageNumber, setPageNumber] = useState<string | number>(1);
  const [downloading, setDownloading] = useState(false);
  const requestRef = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    requestRef.current += 1;
    busyRef.current = false;
    setOpened(false);
    setPageNumber(1);
    setDownloading(false);
    return () => { requestRef.current += 1; };
  }, [pdf, active]);

  const validPage = typeof pageNumber === "number" && Number.isInteger(pageNumber) &&
    pageNumber >= 1 && pageNumber <= (pdf?.numPages ?? 0);

  const downloadPage = async () => {
    if (!pdf || !active || !validPage || busyRef.current) return;
    const request = ++requestRef.current;
    busyRef.current = true;
    setDownloading(true);
    try {
      const original = await pdf.getData();
      if (request !== requestRef.current) return;
      const bytes = await extractPdfPage(original, Number(pageNumber));
      if (request !== requestRef.current) return;
      savePdfDownload(bytes, singlePageFilename(filename, Number(pageNumber)));
      setOpened(false);
    } catch (error) {
      if (request !== requestRef.current) return;
      console.error("Failed to download PDF page:", error);
      toast.error("Unable to download this page. Please try again or download the full PDF.");
    } finally {
      if (request === requestRef.current) {
        busyRef.current = false;
        setDownloading(false);
      }
    }
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end"
      width={260} shadow="md" zIndex={10000000001} trapFocus returnFocus>
      <Popover.Target>
        <button type="button" disabled={!active || !pdf}
          aria-expanded={opened} aria-haspopup="dialog"
          title={!pdf ? "Loading PDF pages…" : "Download a single PDF page"}
          onClick={() => setOpened(value => !value)}
          className="text-sm w-fit bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded disabled:opacity-50 disabled:cursor-not-allowed">
          Download page
        </button>
      </Popover.Target>
      <Popover.Dropdown role="dialog" aria-label="Download a PDF page">
        <Stack gap="sm">
          <NumberInput label={`Page to download (1–${pdf?.numPages ?? 0})`}
            value={pageNumber} onChange={setPageNumber} min={1} max={pdf?.numPages ?? 1}
            allowDecimal={false} allowNegative={false} disabled={downloading}
            data-autofocus />
          <Text size="xs" c="dimmed">Save one page as a separate PDF.</Text>
          <Button onClick={downloadPage} loading={downloading} disabled={!validPage || !active}>
            {validPage ? `Download page ${pageNumber}` : "Choose a page"}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
